import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { GenerationCard } from "@/components/create/GenerationCard";
import { PublishDialog } from "@/components/create/PublishDialog";
import { StudioHeader } from "@/components/create/explore/StudioHeader";
import { imageOpen, useGenerations, useMediaConfig, usePresets } from "@/lib/studio";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "All", test: () => true },
  { key: "images", label: "Images", test: (g) => g.kind === "image" && g.operation !== "edit" },
  { key: "edits", label: "Edits", test: (g) => g.operation === "edit" },
  { key: "videos", label: "Videos", test: (g) => g.kind === "video" },
];

export default function AssetsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [publishing, setPublishing] = useState(null);
  const config = useMediaConfig().data;
  const gens = useGenerations();
  const presetsQ = usePresets();
  const items = gens.data?.items || [];
  const mine = Object.fromEntries((presetsQ.data?.items || []).filter((p) => p.mine && p.generation_id).map((p) => [p.generation_id, p]));
  const shown = items.filter(FILTERS.find((f) => f.key === filter).test);
  const edit = (g) => navigate("/create/image?mode=edit", { state: { sources: [{ id: g.id, preview_url: g.thumb_url }] } });
  return (
    <div className="h-full overflow-y-auto" data-testid="assets-page">
      <StudioHeader title="Assets" back />
      <div className="mx-auto w-full max-w-6xl space-y-5 px-4 pb-20 pt-5 sm:px-6 sm:pt-8">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter assets" data-testid="assets-filters">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} onClick={() => setFilter(f.key)} data-testid={`assets-filter-${f.key}`}
              className={cn("h-8 rounded-full px-3.5 text-xs font-medium transition-colors", filter === f.key ? "bg-white text-ink-950" : "border border-white/10 text-zinc-400 hover:text-zinc-100")}>
              {f.label} {!gens.isLoading && <span className="ml-1 tabular-nums opacity-60">{items.filter(f.test).length}</span>}
            </button>
          ))}
        </div>
        {gens.isLoading && <div className="flex h-40 items-center justify-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {!gens.isLoading && shown.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 px-6 py-14 text-center" data-testid="assets-empty">
            <p className="text-sm text-zinc-300">Nothing here yet.</p>
            <p className="mt-1 text-xs text-zinc-500">Everything you create or edit is saved here.</p>
            <Link to="/create" className="mt-4 inline-flex h-9 items-center rounded-full bg-white px-4 text-xs font-semibold text-ink-950" data-testid="assets-empty-explore-link">Explore presets</Link>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="assets-grid">
          {shown.map((g) => (
            <GenerationCard key={g.id} g={g} preset={mine[g.id]} onEdit={imageOpen(config) ? edit : null} onPublish={setPublishing} />
          ))}
        </div>
      </div>
      <PublishDialog generation={publishing} categories={presetsQ.data?.categories || []} onOpenChange={(o) => !o && setPublishing(null)} />
    </div>
  );
}
